/**
 * A área de contato com o desenvolvedor.
 *
 * É o canal direto de quem usa a plataforma para quem a desenvolve: sugestões,
 * pedidos de mudança e dúvidas. A mensagem fica guardada e chega por e-mail ao
 * administrador. Não exige conta.
 */

import { useState } from 'react';
import { Check, Loader2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';

export function Contato() {
  const { usuario } = useAuth();
  const [nome, setNome] = useState(usuario?.nome ?? '');
  const [email, setEmail] = useState(usuario?.email ?? '');
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState('');

  const submeter = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro('');
    if (nome.trim().length < 2) {
      setErro('Informe seu nome.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setErro('Informe um e-mail válido.');
      return;
    }
    if (mensagem.trim().length < 3) {
      setErro('Escreva a sua mensagem.');
      return;
    }

    setEnviando(true);
    try {
      await api.enviarContato({ nome: nome.trim(), email: email.trim(), mensagem: mensagem.trim() });
      setEnviado(true);
      setMensagem('');
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível enviar agora.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Fale com o desenvolvedor</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Sugira uma solução, peça uma mudança ou tire uma dúvida. Sua mensagem chega direto a quem
          desenvolve a plataforma.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="size-4 text-primary" />
            Contato
          </CardTitle>
          <CardDescription>
            Não precisa ter conta — mas se você estiver logado, já preenchemos seu nome e e-mail.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {enviado ? (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-sm text-success">
                <Check className="size-4" />
                Mensagem enviada. Obrigado!
              </p>
              <Button variant="contorno" onClick={() => setEnviado(false)}>
                Enviar outra
              </Button>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={submeter}>
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo rotulo="Seu nome">
                  <Input
                    required
                    value={nome}
                    onChange={(evento) => setNome(evento.target.value)}
                    placeholder="Como devo te chamar"
                  />
                </Campo>
                <Campo rotulo="Seu e-mail">
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(evento) => setEmail(evento.target.value)}
                    placeholder="voce@exemplo.com"
                  />
                </Campo>
              </div>
              <Campo rotulo="Mensagem" dica="O que você quer ver na plataforma?">
                <textarea
                  required
                  value={mensagem}
                  onChange={(evento) => setMensagem(evento.target.value)}
                  rows={6}
                  className="w-full rounded-md border border-input bg-background p-3 text-sm"
                  placeholder="Escreva a sua sugestão, pedido ou dúvida..."
                />
              </Campo>
              {erro ? <p className="text-xs text-destructive">{erro}</p> : null}
              <Button type="submit" disabled={enviando}>
                {enviando ? <Loader2 className="animate-spin" /> : <MessageCircle />}
                Enviar mensagem
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
